import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { ISyncQueueItem } from "@home-health-notify/models";
import ProgressLoader from "../loader/ProgressLoader";
import {
  ListingTableCell,
  ListingTableContainer,
  ListingTableLayout,
  PatientName,
} from "../common/ListingTable.style";
import SyncQueueStatusBadge from "./SyncQueueStatusBadge";
import { Reviewed } from "./EmrPatients.style";
import {
  formatDateTime,
  patientName,
  patientPath,
  servicesSummary,
} from "./syncQueueUtils";

const columns = [
  { id: "patient", label: "Patient", width: "26%" },
  { id: "mrn", label: "MRN", width: "12%" },
  { id: "status", label: "Status", width: "16%" },
  { id: "mode", label: "Mode", width: "10%" },
  { id: "reviewed", label: "Reviewed", width: "20%" },
  { id: "created", label: "Created", width: "16%" },
];

type HistoryTableProps = {
  items: ISyncQueueItem[];
  isLoading: boolean;
  orgId: string;
  onRowClick: (item: ISyncQueueItem) => void;
};

function PatientCell({ item, orgId }: { item: ISyncQueueItem; orgId: string }) {
  const name = patientName(item);
  const linkable =
    (item.status === "added" || item.status === "skipped") && item.hhnEntityId;
  return (
    <>
      {linkable ? (
        <PatientName noWrap>
          <Link
            component={RouterLink}
            to={patientPath(orgId, item.hhnEntityId as string)}
            underline="hover"
            onClick={e => e.stopPropagation()}
            data-testid="history-patient-link"
          >
            {name}
          </Link>
        </PatientName>
      ) : (
        <PatientName noWrap>{name}</PatientName>
      )}
      <Typography variant="caption" color="text.secondary">
        {servicesSummary(item)}
      </Typography>
    </>
  );
}

function reviewedCell(item: ISyncQueueItem) {
  if (item.reviewedBy) {
    return (
      <Reviewed>
        <Typography variant="body2">{item.reviewedBy}</Typography>
        {item.reviewedAt && (
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(item.reviewedAt)}
          </Typography>
        )}
      </Reviewed>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {item.syncMode === "auto" ? "Auto-synced" : "—"}
    </Typography>
  );
}

export function HistoryTable({
  items,
  isLoading,
  orgId,
  onRowClick,
}: HistoryTableProps) {
  return (
    <ListingTableLayout>
      <ListingTableContainer>
        <Table stickyHeader data-testid="history-table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  style={{ width: column.width, padding: "10px" }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <ProgressLoader />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{ textAlign: "center" }}
                >
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              items.map(item => (
                <TableRow
                  key={item._id}
                  hover
                  sx={{ cursor: "pointer" }}
                  data-testid="history-row"
                  onClick={() => onRowClick(item)}
                >
                  <ListingTableCell data-testid="patient-name">
                    <PatientCell item={item} orgId={orgId} />
                  </ListingTableCell>
                  <ListingTableCell data-testid="mrn">
                    {item.emrId}
                  </ListingTableCell>
                  <ListingTableCell>
                    <SyncQueueStatusBadge status={item.status} />
                  </ListingTableCell>
                  <ListingTableCell sx={{ textTransform: "capitalize" }}>
                    {item.syncMode}
                  </ListingTableCell>
                  <ListingTableCell>{reviewedCell(item)}</ListingTableCell>
                  <ListingTableCell>
                    {formatDateTime(item.createdAt)}
                  </ListingTableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ListingTableContainer>
    </ListingTableLayout>
  );
}
